import axios from "axios";
import { X_API_KEY } from "../constants";
import IdentityClient from "./IdentityClient";
import { Cache } from "../util/Cache.util";
import OfflineInterceptor from "../util/OfflineInterceptor";
import OfflineSyncQueue from "../util/OfflineSyncQueue";
import Dialog from "../util/Dialog";
import { haptics } from "../util/haptics";

export class LiveApiCache {
	static #AUTH_EXPIRATION_SECS = 60 * 60 * 24 * 60; // match Live's "auth" expiration
	/** @type Promise */
	static SharedTokenFetch;
	
	static Set(token) {
		if (!token) {
			return;
		}
		Cache.Local().set("api-token", { token, expires_on: this.#Get_Expiration() });
	}

	static Get() {
		const stored = Cache.Local().get("api-token");
		if (stored?.expires_on > Date.now()) {
			return stored.token;
		}
		LiveApiCache.Reset();
	}

	static Reset() {
		Cache.Local().set("api-token", undefined);
	}

	static #Get_Expiration() {
		return Date.now() + (this.#AUTH_EXPIRATION_SECS * 1000);
	}
}

// Dev API override: ?api=URL sets it, persists in sessionStorage.
// ?api=reset clears it. Shown as a banner when active.
const _isDev = process.env.NODE_ENV !== 'production';
const _apiParam = _isDev && new URLSearchParams(window.location.search).get('api');
if (_apiParam === 'reset') {
	sessionStorage.removeItem('__api_override');
} else if (_apiParam) {
	sessionStorage.setItem('__api_override', _apiParam);
}
const _apiOverride = _isDev && sessionStorage.getItem('__api_override');
const _apiBaseURL = _apiOverride || process.env.REACT_APP_API_CLIENT_BASE_URL || "https://staging.bosslogics.live/api";

if (_isDev) {
	console.warn(`%c[API] ${_apiBaseURL}` + (_apiOverride ? '  (override — ?api=reset to clear)' : ''), 'color:#0099ff;font-weight:bold');
	if (_apiOverride) {
		const banner = document.createElement('div');
		banner.innerHTML = `API: <b>${_apiOverride}</b> <span style="opacity:0.6;margin-left:6px">· ?api=reset</span>`;
		Object.assign(banner.style, {
			position: 'fixed', bottom: '4px', left: '4px', zIndex: '99999',
			background: '#1e40af', color: '#fff', fontSize: '10px', padding: '2px 8px',
			borderRadius: '4px', opacity: '0.85', pointerEvents: 'none',
		});
		document.body?.appendChild(banner) || window.addEventListener('DOMContentLoaded', () => document.body.appendChild(banner));
	}
}

const LiveApiClient = axios.create({
	baseURL: _apiBaseURL,
	headers: {
		Authorization: "",
		Project: "together.boss",
		Accept: "application/json",
		'Cache-Control': 'no-cache',
		// 'Pragma': 'no-cache',
		'Expires': '0',
	},
});

// One-shot capture of ?recache URL param. The flag persists for the lifetime
// of this page so every axios request cascades `__recache-page=1` as a query
// param — Live PHP reads it from $_REQUEST (see boss/src/php/controllers/item_result.php),
// not from headers, so sending it as a header is invisible to the backend.
// URL is stripped after capture so a refresh / shared link / bookmark doesn't
// keep re-triggering recache.
const __recache_active = new URLSearchParams(window.location.search).has('recache');
if (__recache_active) {
	const url = new URL(window.location.href);
	url.searchParams.delete('recache');
	window.history.replaceState(null, '', url);
}

LiveApiClient.interceptors.request.use(
	async config => {
		if (__recache_active) {
			config.params = config.params || {};
			config.params['__recache-page'] = 1;
		}

		// Default count=false for standard GET requests (skip expensive count queries)
		// Excludes fn/ endpoints (custom functions) and /count endpoints (explicit count requests)
		if (config.method === 'get' && !config.url?.includes('/fn/') && !config.url?.endsWith('/count')) {
			config.params = config.params || {};
			if (config.params.count === undefined) {
				config.params.count = false;
			}
		}

		const savedToken = LiveApiCache.Get();
		
		const checkToken = token => {
			const base64Url = token.split('.').at(1);
			if (!base64Url) return false;
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

			const jsonPayload = decodeURIComponent(window.atob(base64).split('')
				.map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
			if (!jsonPayload) {
				return false;
			}
			
			try {
				const payload = JSON.parse(jsonPayload);
				// 60s buffer for clock skew between client and server
				return !(payload.exp && (payload.exp * 1000 - 60000) < Date.now());
			} catch (e) {
				return false;
			}
		}
		
		// X_API_KEY takes precedence over a saved session token so that public
		// share-links (?xApiKey=...) behave consistently for both anonymous and
		// logged-in visitors.
		if (X_API_KEY || config.headers['x-api-key']) {
			if (X_API_KEY) config.headers['x-api-key'] = X_API_KEY;
			return config;
		}

		if (savedToken && checkToken(savedToken)) {
			config.headers.Authorization = savedToken || '';
			return config;
		}

		LiveApiCache.SharedTokenFetch ??= IdentityClient.post("getToken")
			.finally(() => { LiveApiCache.SharedTokenFetch = undefined; });

		const { data: { token } } = await LiveApiCache.SharedTokenFetch;
		config.headers.Authorization = token;
		LiveApiCache.Set(token);

		Object.keys(config.headers).forEach(key => {
			if (config.headers[key] === undefined) {
				delete config.headers[key];
			}
		});

		return config;
	},
	error => Promise.reject(error)
);

/** Shared refresh promise so concurrent 401s only trigger one getToken call */
let _refresh_promise = null;

/**
 * Tracks consecutive network failures so a single transient error (slow query,
 * one fragile request) doesn't trip the global "Connection lost" toaster while
 * the browser is still online. Reset on any successful response.
 */
let _consecutive_network_errors = 0;
let _last_network_error_at = 0;
const NETWORK_ERROR_WINDOW_MS = 15_000;
const NETWORK_ERROR_THRESHOLD = 2;

LiveApiClient.interceptors.response.use(
	response => {
		_consecutive_network_errors = 0;
		return response;
	},
	async error => {
		// Ignore cancelled/aborted requests (e.g., from AbortController)
		if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
			return Promise.reject(error);
		}

		// Handle network errors (no response = no internet or server unreachable).
		// Callers can opt out of the global toaster by setting
		// `silenceConnectionToast: true` on the request config — useful for
		// background polling that handles its own failures locally.
		if (!error.response) {
			const now = Date.now();
			if (now - _last_network_error_at > NETWORK_ERROR_WINDOW_MS) {
				_consecutive_network_errors = 0;
			}
			_consecutive_network_errors += 1;
			_last_network_error_at = now;

			// Suppress the toast unless we're confident this is a real outage:
			//   - browser explicitly reports offline, OR
			//   - we've seen multiple network errors in quick succession.
			// Timeouts (ECONNABORTED/ETIMEDOUT) by themselves usually mean a slow
			// query, not a lost connection — don't show the toast for a single one.
			const browserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
			const sustained = _consecutive_network_errors >= NETWORK_ERROR_THRESHOLD;
			const shouldNotify = !error.config?.silenceConnectionToast && (browserOffline || sustained);

			if (shouldNotify) {
				haptics.error();
				Dialog.fire({
					toast: true,
					position: 'top-end',
					icon: 'error',
					title: 'Connection lost. Please check your internet.',
					showConfirmButton: false,
					timer: 5000,
				});
			}
			return Promise.reject(error);
		}

		if (error.response?.status !== 401) {
			return Promise.reject(error);
		}

		const original_config = error.config;

		// A 401 doesn't always mean "session dead." If the request explicitly
		// carried an api-key header, the 401 means "wrong/missing api-key" —
		// not session expiry. If the request had no Authorization header at
		// all, it was a public/anonymous call, also not session-related. In
		// both cases the user's JWT is still valid, so we surface the 401 to
		// the caller without clearing localStorage or redirecting to /login.
		const h = original_config?.headers || {};
		const hasApiKey = !!(h["x-api-key"] || h["X-Api-Key"] || h["api-key"] || h["Api-Key"]);
		const hasAuth   = !!(h["Authorization"] || h["authorization"]) && h["Authorization"] !== "";
		if (hasApiKey || !hasAuth) {
			return Promise.reject(error);
		}

		// Also: 401 on a Catalog branding lookup (e.g. /Catalog/:something:something)
		// is an unrelated public-API failure and must not log out an admin who's
		// in the middle of setup. Recognize the catalog-by-uri shape and bail.
		const reqUrl = String(original_config?.url || "");
		if (/^\/?Catalog\/:/.test(reqUrl)) {
			return Promise.reject(error);
		}

		// Returns true if the function actually triggered a redirect; false if
		// it bailed (e.g. on /demo/* or a public auth page). Callers use the
		// return to decide whether to surface the 401 to the original caller
		// (so app-level promises can resolve) or stall as the page navigates.
		const clearSessionAndRedirect = () => {
			LiveApiCache.Reset();
			localStorage.removeItem('catalog_invitation:data');
			localStorage.removeItem("organization_member:data");
			localStorage.removeItem("currentUser");
			if (typeof window !== 'undefined') {
				const path = window.location?.pathname || '';
				// Don't kick the user to /login from inside /demo/* — the
				// booth stations have their own "session expired" UX and a
				// hard navigation would scrap the kiosk-mode experience
				// mid-demo. The 401 still propagates to the caller's catch.
				//
				// Don't redirect-loop when the user is *already* on a public
				// auth page. A 401 on /login or /register just means "not
				// logged in" — the expected state. Without this guard the
				// interceptor sets window.location = '/login?redirect=/login'
				// and refreshing keeps re-firing the same call.
				if (path.startsWith('/demo/') ||
				    path === '/login' ||
				    path === '/register' ||
				    path === '/reset-password' ||
				    path === '/forgot-password' ||
				    path.startsWith('/claim')) {
					return false;
				}
			}
			window.location = '/login?redirect=' + window.location.pathname;
			return true;
		};

		// Preflight requests can't be refreshed/retried meaningfully
		if (original_config.method === "options") {
			return Promise.reject(error);
		}

		// Already refreshed once and the retry came back 401 — usually means
		// session is dead. But before clearing, look at the response body: if
		// the failure is "API Key required" or another endpoint-permission
		// problem (not a session/auth problem), the user's session is still
		// valid and we should surface the 401 instead of logging them out.
		if (original_config._retry) {
			const desc = String(
				error.response?.data?.errors?.[0]?.descr ||
				error.response?.data?.error ||
				error.response?.data?.message ||
				""
			).toLowerCase();
			const isEndpointPermission = desc.includes("api key") || desc.includes("api-key");
			if (isEndpointPermission) {
				return Promise.reject(error);
			}
			// If clearSessionAndRedirect bailed (no redirect — /demo/*
			// or a public auth page), surface the 401 to the caller so
			// the page can handle it. Otherwise stall while the redirect
			// takes effect.
			if (!clearSessionAndRedirect()) {
				return Promise.reject(error);
			}
			return new Promise(() => {}); // page is navigating away
		}

		// If using a static X_API_KEY (microsites), no token refresh is possible
		if (X_API_KEY) {
			return Promise.reject(error);
		}

		// Attempt token refresh and retry the original request
		original_config._retry = true;

		try {
			_refresh_promise ??= IdentityClient.post("getToken")
				.finally(() => { _refresh_promise = null; });
			const { data: { token } } = await _refresh_promise;

			if (!token) {
				// Session truly expired — redirect silently. Don't throw: the
				// old "No token returned" throw was caught here and re-rejected,
				// which bubbled up as Sentry noise (JS-5F9 cluster) even though
				// the UX is correct (user just gets logged out).
				//
				// If clearSessionAndRedirect bailed (no redirect — /demo/* or
				// a public auth page), surface the 401 so callers like the
				// branding fetch resolve their promise instead of hanging.
				if (!clearSessionAndRedirect()) {
					return Promise.reject(error);
				}
				return new Promise(() => {}); // page is navigating away
			}

			LiveApiCache.Set(token);
			original_config.headers.Authorization = token;
			return LiveApiClient(original_config);
		} catch (refresh_error) {
			// Genuine refresh failure (network, server error) — surface it
			// for debugging in addition to logging the user out.
			clearSessionAndRedirect();
			return Promise.reject(refresh_error);
		}
	}
);

// Install offline interceptor (caching + offline fallback + write queuing)
OfflineInterceptor.Install(LiveApiClient);
OfflineSyncQueue.Init(LiveApiClient);

export default LiveApiClient;
